import {
  collection,
  doc,
  onSnapshot,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../platform/firebase/clients';
import { logError } from './logger';
import { wrapSubscriptionError } from './subscriptionError';

// -----------------------------------------------------------------------------
// Types — keep this file aligned with functions/services/providers/* and
// functions/services/llmUsage.js. This is the frontend-side source of truth
// for the LLM stream/usage shape across providers.
// -----------------------------------------------------------------------------

export type LlmProvider = 'anthropic' | 'openai' | 'together' | 'gemini';

export type AnthropicModelId =
  | 'claude-opus-5'
  | 'claude-opus-4-7'
  | 'claude-sonnet-4-6'
  | 'claude-haiku-4-5'
  // Predecessors — retained for in-flight jobs pinned to the prior generation.
  | 'claude-opus-4-6'
  | 'claude-sonnet-4-5';

export type OpenAIModelId =
  // Current generation (GPT-5.6 Sol / GPT-5.5 / 5.4)
  | 'gpt-5.6-sol'
  | 'gpt-5.5'
  | 'gpt-5.5-pro'
  | 'gpt-5.4'
  | 'gpt-5.4-pro'
  | 'gpt-5.4-mini'
  | 'gpt-5.4-nano'
  // Predecessor — still active in OpenAI's API with no shutdown date.
  | 'gpt-5'
  | 'gpt-5-mini'
  | 'gpt-5-nano';

export type TogetherModelId =
  | 'moonshotai/Kimi-K3'
  | 'zai-org/GLM-5.2'
  | 'MiniMaxAI/MiniMax-M2.7';

export type GeminiModelId =
  // Current (3.x)
  | 'gemini-3.7-flash'
  | 'gemini-3.1-pro-preview'
  | 'gemini-3-flash-preview'
  | 'gemini-3.1-flash-lite'
  // Predecessor — deprecates 2026-06-17. Kept for in-flight pinned jobs.
  | 'gemini-2.5-pro';

export type LlmModelId = AnthropicModelId | OpenAIModelId | TogetherModelId | GeminiModelId;

/**
 * How a model uses "thinking" tokens.
 * - `'none'`: never spends thinking time.
 * - `'optional'`: accepts the explicit `thinking` parameter; spends thinking
 *   time only when the caller enables it.
 * - `'adaptive'`: spends thinking time automatically per request. Rejects
 *   the `thinking` parameter (Opus 4.7).
 * - `'always'`: thinks on every request, no toggle. Caller picks DEPTH via
 *   `effort`, not whether thinking runs (Gemini 3.x, Together reasoning
 *   models).
 */
export type ThinkingMode = 'none' | 'optional' | 'adaptive' | 'always';

export interface ModelConfig {
  provider: LlmProvider;
  thinkingMode: ThinkingMode;
  // Allowed `effort` values for this model, or `null` if it doesn't accept
  // an effort enum (Anthropic rejected with `invalid-argument` if you pass
  // one outside this list; Gemini 2.5 Pro / Together rejected outright).
  // Mirrors functions/utils/llmModels.js `supportedEffortLevels`.
  supportedEffortLevels: string[] | null;
}

// Frontend-side model registry. Mirrors the server's `functions/utils/llmModels.js`
// capability flags for the config the client needs. Every model the backend
// supports must have an entry here too (model ids from different providers
// can collide in prefix, so parsing is unsafe).
const ANTHROPIC_EFFORTS: ReadonlyArray<string> = ['low', 'medium', 'high'];
const OPENAI_5_4_EFFORTS: ReadonlyArray<string> = ['low', 'medium', 'high', 'xhigh'];
const OPENAI_5_EFFORTS: ReadonlyArray<string> = ['minimal', 'low', 'medium', 'high'];
const GEMINI_3X_EFFORTS: ReadonlyArray<string> = ['minimal', 'low', 'medium', 'high'];
// Gemini 3.1 Pro: low|medium|high. No 'minimal' — Pro can't disable thinking.
// On the live API 'high' triggers "Deep Think Mini" — that IS the maximum;
// xhigh is OpenAI-only (verified live 2026-05-14).
const GEMINI_3_PRO_EFFORTS: ReadonlyArray<string> = ['low', 'medium', 'high'];
const GEMINI_3_FLASH_37_EFFORTS: ReadonlyArray<string> = ['low', 'medium', 'high'];

const MODEL_CONFIGS: Record<LlmModelId, ModelConfig> = {
  'claude-opus-5':     { provider: 'anthropic', thinkingMode: 'adaptive', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-opus-4-7':   { provider: 'anthropic', thinkingMode: 'adaptive', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-sonnet-4-6': { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-haiku-4-5':  { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-opus-4-6':   { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  'claude-sonnet-4-5': { provider: 'anthropic', thinkingMode: 'optional', supportedEffortLevels: [...ANTHROPIC_EFFORTS] },
  // OpenAI GPT-5.x — all reasoning-capable. thinkingMode 'optional' means
  // callers opt in via the `thinking` parameter; depth comes from `effort`.
  // GPT-5.4 / 5.5 accept the wider 'xhigh' tier; GPT-5 caps at 'high'.
  'gpt-5.6-sol':   { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.5':       { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.5-pro':   { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4':       { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4-pro':   { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4-mini':  { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5.4-nano':  { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_4_EFFORTS] },
  'gpt-5':         { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_EFFORTS] },
  'gpt-5-mini':    { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_EFFORTS] },
  'gpt-5-nano':    { provider: 'openai', thinkingMode: 'optional', supportedEffortLevels: [...OPENAI_5_EFFORTS] },
  'moonshotai/Kimi-K3':     { provider: 'together', thinkingMode: 'always', supportedEffortLevels: ['low', 'high', 'max'] },
  'zai-org/GLM-5.2':         { provider: 'together', thinkingMode: 'always', supportedEffortLevels: ['high', 'max'] },
  'MiniMaxAI/MiniMax-M2.7': { provider: 'together', thinkingMode: 'none', supportedEffortLevels: null },
  // Gemini 3.x — always-thinking; the adapter sends thinkingConfig.thinkingLevel
  // from the `effort` field. Pro accepts xhigh; flash variants cap at high.
  'gemini-3.7-flash':          { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3_FLASH_37_EFFORTS] },
  'gemini-3.1-pro-preview':  { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3_PRO_EFFORTS] },
  'gemini-3-flash-preview':  { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3X_EFFORTS] },
  'gemini-3.1-flash-lite':   { provider: 'gemini', thinkingMode: 'always', supportedEffortLevels: [...GEMINI_3X_EFFORTS] },
  // Gemini 2.5 Pro (predecessor — deprecates 2026-06-17). Uses budget_tokens,
  // not effort — supportedEffortLevels: null mirrors the server registry.
  'gemini-2.5-pro':          { provider: 'gemini', thinkingMode: 'optional', supportedEffortLevels: null },
};

export function getModelConfig(id: LlmModelId): ModelConfig {
  return MODEL_CONFIGS[id];
}

export function getProviderForModel(id: LlmModelId): LlmProvider {
  return MODEL_CONFIGS[id].provider;
}

export function getSupportedEffortLevels(id: LlmModelId): string[] | null {
  return MODEL_CONFIGS[id].supportedEffortLevels;
}

/**
 * Whether `effort` will actually be sent to the upstream API for this model
 * given whether `thinking` is currently enabled by the caller.
 *
 * Anthropic + OpenAI optional-thinking: adapters send the effort /
 * output_config / reasoning field ONLY when `params.thinking` is present.
 * Without thinking, effort is silently dropped — picking it would be a no-op.
 *
 * Gemini 3.x (`thinkingMode: 'always'`): the adapter always sends
 * `thinkingConfig.thinkingLevel` from effort, defaulting to 'medium'.
 * Together reasoning models (`thinkingMode: 'always'`): the adapter sends
 * `reasoning_effort` from effort.
 *
 * Opus 4.7 (`thinkingMode: 'adaptive'`): the model picks reasoning depth
 * itself per-request — there is no caller-side knob. Effort doesn't apply.
 *
 * Together MiniMax (`thinkingMode: 'none'`): no reasoning at all.
 *
 * Use this to decide whether to SHOW the effort picker.
 */
export function effortIsActive(id: LlmModelId, thinkingEnabled: boolean): boolean {
  const cfg = MODEL_CONFIGS[id];
  if (cfg.supportedEffortLevels === null) return false;
  if (cfg.thinkingMode === 'always') return true;       // Gemini 3.x / Together reasoning
  if (cfg.thinkingMode === 'optional') return thinkingEnabled;
  return false;  // 'adaptive' picks itself; 'none' doesn't reason.
}

export type LlmStreamStatus = 'thinking' | 'responding' | 'done' | 'error';

export interface LlmStreamServerToolUse {
  webSearchRequests: number;
  webFetchRequests: number;
  codeExecutionRequests: number;
}

export interface LlmStreamUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  serverToolUse?: LlmStreamServerToolUse;
}

export interface LlmStreamContext {
  taskId: string | null;
  taskType: string | null;
  campaignId: string | null;
}

export interface LlmStreamDoc {
  id: string;
  userId: string;
  userEmail: string | null;
  provider: LlmProvider;
  model: LlmModelId;
  status: LlmStreamStatus;
  thinking: string;
  text: string;
  errorMessage: string | null;
  errorStatus: number | null;
  usage: LlmStreamUsage | null;
  context: LlmStreamContext;
  createdAt: Date | null;
  lastUpdateAt: Date | null;
  expiresAt: Date | null;
  /** Terminal accounting state written by the server-authoritative command. */
  billingStatus?: 'recorded' | 'missing';
  usageDocId?: string;
  budgetStatus?: 'unsettled';
}

// Mime types the server-side validator accepts. Mirrors
// `ALLOWED_ATTACHMENT_MIME_TYPES` in functions/services/llmStream.js.
export type LlmAttachmentMimeType =
  | 'image/png'
  | 'image/jpeg'
  | 'image/gif'
  | 'image/webp'
  | 'application/pdf'
  | 'text/plain';

/**
 * Attachment to send alongside a prompt. `data` is the raw base64 string (no
 * `data:` prefix — the adapter assembles the data URL for OpenAI and the
 * inlineData object for Gemini). The server caps the decoded size at 2MB per
 * attachment — see ATTACHMENT_MAX_BYTES in functions/services/llmStream.js.
 *
 * Use `fileToLlmAttachment` to build one from a browser File / Blob without
 * having to do the FileReader dance at the callsite.
 */
export interface LlmAttachment {
  mimeType: LlmAttachmentMimeType;
  data: string;
  name?: string;
}

export const LLM_ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Convert a browser File / Blob to an LlmAttachment. Rejects if the file is
 * over the 2MB cap (mirrors the server check) or has a mime type the server
 * doesn't accept — failing client-side avoids a wasted callable round-trip.
 */
export async function fileToLlmAttachment(file: File): Promise<LlmAttachment> {
  if (file.size > LLM_ATTACHMENT_MAX_BYTES) {
    throw new Error(
      `Attachment "${file.name}" is ${file.size} bytes, over the ${LLM_ATTACHMENT_MAX_BYTES}-byte limit.`,
    );
  }
  const allowed: LlmAttachmentMimeType[] = [
    'image/png', 'image/jpeg', 'image/gif', 'image/webp',
    'application/pdf', 'text/plain',
  ];
  if (!allowed.includes(file.type as LlmAttachmentMimeType)) {
    throw new Error(
      `Attachment "${file.name}" has unsupported mime type "${file.type}". Allowed: ${allowed.join(', ')}.`,
    );
  }
  const buf = await file.arrayBuffer();
  // btoa over a binary string is the smallest base64 encoder available in the
  // browser; chunked because String.fromCharCode(...big array) blows the stack.
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return {
    mimeType: file.type as LlmAttachmentMimeType,
    data: btoa(binary),
    name: file.name,
  };
}

export interface ChatLlmStreamRequest {
  streamDocId: string;
  message: string;
  model: LlmModelId;
  systemMessage?: string;
  maxTokens?: number;
  thinking?: { type: 'enabled'; budget_tokens: number } | null;
  effort?: string | null;
  tools?: Array<Record<string, unknown>> | null;
  attachments?: LlmAttachment[] | null;
  context?: Partial<LlmStreamContext>;
}

export interface ChatLlmStreamResult {
  streamDocId: string;
  status: 'done' | 'error';
}

// -----------------------------------------------------------------------------
// Client-generated doc id. The callable requires the caller to create this id
// client-side and pass it in, so the UI can subscribe to the doc via
// onSnapshot *before* the callable resolves — otherwise the first thinking
// deltas land before the listener is attached and the UI shows a gap.
// -----------------------------------------------------------------------------

export function newStreamDocId(): string {
  return doc(collection(db, 'llm_streams')).id;
}

// -----------------------------------------------------------------------------
// Callable dispatcher. Picks the right Cloud Function name based on the
// model's provider so callers don't care which provider serves which model.
// The handler returns only { streamDocId, status } — the actual
// thinking/text stream lives in the Firestore doc subscribed to below.
// -----------------------------------------------------------------------------

const CALLABLE_NAMES: Record<LlmProvider, string> = {
  anthropic: 'chatAnthropicStream',
  openai: 'chatOpenAIStream',
  together: 'chatTogetherStream',
  gemini: 'chatGeminiStream',
};

// Firebase callable SDK default is 70s — not enough for streaming LLM calls
// with extended thinking + web search. The server caps at 900s (see
// `timeoutSeconds` in chatAnthropicStream / chatTogetherStream). We bump the
// client to match: 15 min when the run is expected to spend thinking time,
// 5 min otherwise.
const LLM_TIMEOUT_DEFAULT_MS = 5 * 60 * 1000;
const LLM_TIMEOUT_THINKING_MS = 15 * 60 * 1000;

function willSpendThinking(req: ChatLlmStreamRequest): boolean {
  const { thinkingMode } = getModelConfig(req.model);
  if (thinkingMode === 'adaptive') return true;
  if (thinkingMode === 'always') return true;
  if (thinkingMode === 'optional') return req.thinking?.type === 'enabled';
  return false;
}

export async function chatLlmStream(
  req: ChatLlmStreamRequest,
): Promise<ChatLlmStreamResult> {
  const provider = getProviderForModel(req.model);
  const timeout = willSpendThinking(req) ? LLM_TIMEOUT_THINKING_MS : LLM_TIMEOUT_DEFAULT_MS;
  const fn = httpsCallable<ChatLlmStreamRequest, ChatLlmStreamResult>(
    functions,
    CALLABLE_NAMES[provider],
    { timeout },
  );
  try {
    const res = await fn(req);
    return res.data;
  } catch (err: any) {
    // invalid-argument almost always means the server rejected the model or
    // parameters — most commonly a client/server registry mismatch where the
    // client offered a model the server doesn't allowlist. Surface a clearer
    // message; other errors (timeout, network, unauthenticated) pass through.
    if (err?.code === 'functions/invalid-argument') {
      const message = typeof err.message === 'string' ? err.message : '';
      if (!/unknown model|model is required/i.test(message)) {
        throw err;
      }
      throw new Error(
        `Model "${req.model}" is not available on the server. ${message}`,
      );
    }
    throw err;
  }
}

// -----------------------------------------------------------------------------
// Firestore subscription. Returns the standard onSnapshot unsubscribe — the
// caller must clean up, usually in a useEffect return.
// -----------------------------------------------------------------------------

function toDateOrNull(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function toNumberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function mapUsage(raw: unknown): LlmStreamUsage | null {
  if (!raw || typeof raw !== 'object') return null;
  const u = raw as Record<string, any>;
  const stu = u.serverToolUse && typeof u.serverToolUse === 'object' ? u.serverToolUse : null;
  return {
    inputTokens: toNumberOr(u.inputTokens, 0),
    outputTokens: toNumberOr(u.outputTokens, 0),
    cacheReadTokens: toNumberOr(u.cacheReadTokens, 0),
    cacheWriteTokens: toNumberOr(u.cacheWriteTokens, 0),
    serverToolUse: stu ? {
      webSearchRequests: toNumberOr(stu.webSearchRequests, 0),
      webFetchRequests: toNumberOr(stu.webFetchRequests, 0),
      codeExecutionRequests: toNumberOr(stu.codeExecutionRequests, 0),
    } : undefined,
  };
}

const VALID_STATUSES: LlmStreamStatus[] = ['thinking', 'responding', 'done', 'error'];

// Malformed-doc logger. A bad llm_streams doc should never happen (only the
// admin SDK writes to this collection), so if we see one it's a real bug —
// schema drift, a rules regression, or a migration gone wrong. Routed
// through `logError` so it lands in Cloud Error Reporting (grouped) rather
// than only the raw logs.
function warnMalformed(id: string, reason: string, data: Record<string, any>) {
  const ctx = {
    streamDocId: id,
    reason,
    provider: typeof data.provider === 'string' ? data.provider : null,
    model: typeof data.model === 'string' ? data.model : null,
    status: typeof data.status === 'string' ? data.status : null,
  };
  logError(new Error(`llm_streams doc malformed: ${reason}`), {
    context: 'llm.warnMalformed',
    metadata: ctx,
  });
}

function mapStreamDoc(id: string, data: Record<string, any>): LlmStreamDoc | null {
  if (data.provider !== 'anthropic' && data.provider !== 'openai' && data.provider !== 'together' && data.provider !== 'gemini') {
    warnMalformed(id, 'invalid provider', data);
    return null;
  }
  if (typeof data.model !== 'string') {
    warnMalformed(id, 'invalid model', data);
    return null;
  }
  if (!VALID_STATUSES.includes(data.status)) {
    warnMalformed(id, 'invalid status', data);
    return null;
  }
  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    userEmail: typeof data.userEmail === 'string' ? data.userEmail : null,
    provider: data.provider,
    model: data.model as LlmModelId,
    status: data.status as LlmStreamStatus,
    thinking: typeof data.thinking === 'string' ? data.thinking : '',
    text: typeof data.text === 'string' ? data.text : '',
    errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : null,
    errorStatus: typeof data.errorStatus === 'number' ? data.errorStatus : null,
    usage: mapUsage(data.usage),
    context: {
      taskId: data.context?.taskId ?? null,
      taskType: data.context?.taskType ?? null,
      campaignId: data.context?.campaignId ?? null,
    },
    createdAt: toDateOrNull(data.createdAt),
    lastUpdateAt: toDateOrNull(data.lastUpdateAt),
    expiresAt: toDateOrNull(data.expiresAt),
    billingStatus: data.billingStatus === 'recorded' || data.billingStatus === 'missing'
      ? data.billingStatus
      : undefined,
    usageDocId: typeof data.usageDocId === 'string' ? data.usageDocId : undefined,
    budgetStatus: data.budgetStatus === 'unsettled' ? 'unsettled' : undefined,
  };
}

// Subscribe to a stream doc. Callers should attach this BEFORE calling
// `chatLlmStream` so the listener is in place before the handler's first write
// (otherwise early thinking deltas land before onSnapshot fires and the UI
// shows a gap).
//
// Subscription-leak caveat: if the subsequent `chatLlmStream` call throws
// synchronously (e.g. validation rejection — unknown model, bad params), no
// stream doc will ever exist. The subscription stays live until unmount. If
// the caller's error recovery path shows an error toast and stays on the
// page, unsub in the catch block. Unmount cleanup is sufficient for most
// cases but not all.
export function subscribeToLlmStream(
  streamDocId: string,
  cb: (doc: LlmStreamDoc | null) => void,
): Unsubscribe {
  const ref = doc(db, 'llm_streams', streamDocId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        cb(null);
        return;
      }
      cb(mapStreamDoc(snap.id, snap.data() as Record<string, any>));
    },
    wrapSubscriptionError('llm.subscribeToLlmStream', () => cb(null)),
  );
}
